import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PayrollRunKind, PayrollRunStatus } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const payrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payroll',
  component: PayrollListPage,
});

const STATUS_VARIANT: Record<PayrollRunStatus, 'default' | 'muted' | 'destructive'> = {
  draft: 'muted',
  preview: 'muted',
  locked: 'default',
  posted: 'default',
};

function formatRupees(paise: number): string {
  return (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function PayrollListPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState(currentMonth());
  const [kind, setKind] = React.useState<PayrollRunKind>('regular');
  const [notes, setNotes] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: ['payroll-runs'],
    queryFn: () => api.listPayrollRuns(),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.createPayrollRun({
        month,
        kind,
        notes: notes.trim() === '' ? undefined : notes.trim(),
      }),
    onSuccess: async () => {
      setOpen(false);
      setNotes('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const rows = runsQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Payroll
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Draft → preview → lock → post. Posting writes to the ledger and locks the month's
            allocation matrix.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>New run</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New payroll run</DialogTitle>
              <DialogDescription>
                Only one regular run per month. Off-cycle runs are for corrections.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Month</Label>
                <Input
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.currentTarget.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Kind</Label>
                <Select value={kind} onChange={(e) => setKind(e.currentTarget.value as PayrollRunKind)}>
                  <option value="regular">Regular</option>
                  <option value="off_cycle">Off-cycle adjustment</option>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Notes (optional)</Label>
                <Input value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
              </div>
              {error ? (
                <Alert variant="destructive">
                  <AlertTitle>Could not create</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create draft'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All runs</CardTitle>
          <CardDescription>{rows.length} shown</CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No payroll runs yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rows.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div>
                    <div className="font-medium">
                      <Link to="/payroll/$id" params={{ id: r.id }}>
                        {r.month} · {r.kind === 'regular' ? 'Regular' : 'Off-cycle'}
                      </Link>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {r.itemCount} item(s) · net ₹ {formatRupees(r.totalNetPaise)} · employer cost ₹{' '}
                      {formatRupees(r.totalEmployerCostPaise)}
                    </div>
                  </div>
                  <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

    </AppShell>
  );
}
